import { useState } from "react";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} ClassReportItem
 * @property {number} id - ID of the class
 * @property {string} name - Name of the class
 * @property {number} avg_progress - Average progress
 * @property {number} progress_80_percent - Students with 80% progress
 * @property {number} progress_100_percent - Students with 100% progress
 * @property {number} avg_quiz_score - Average quiz score
 * @property {number} avg_attempts - Average attempts
 */

/**
 * @typedef {object} TeacherDetail
 * @property {number} id - Teacher ID
 * @property {string|null} name - Teacher name
 * @property {string} email - Teacher email
 * @property {number} total_classes - Total classes
 * @property {number} overall_progress - Overall progress
 * @property {number} overall_quiz_pass_rate - Overall quiz pass rate
 * @property {number} completion_rate - Completion rate
 */

/**
 * @typedef {object} ClassesPaginated
 * @property {ClassReportItem[]} data - Array of class report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} TeacherReportByIdData
 * @property {TeacherDetail} teacher - Teacher details
 * @property {ClassesPaginated} classes - Paginated list of classes
 */

/**
 * @typedef {object} MetaType
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

export const useGetTeacherReportById = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
        /** @type {TeacherReportByIdData | null | undefined} */(undefined)
    );
    const [metaData, setMetaData] = useState(
        /** @type {MetaType | null | undefined} */(undefined)
    );

    /**
     * Fetch teacher report by ID with pagination
     * @async
     * @param {number|string} teacherId - Teacher ID to fetch report for
     * @param {number} [page] - Page number for pagination
     * @param {number} [per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getTeacherReportById = async (
        teacherId,
        page = 1,
        per_page = ITEMS_PER_PAGE
    ) => {
        if (!teacherId) {
            setError("Teacher ID is required");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const query = new URLSearchParams();
            query.append("page", page.toString());
            query.append("per_page", per_page.toString());

            const url = `${TENANT_DOMAIN}/school/admin/reports/teachers/${teacherId}?${query.toString()}`;
            const response = await axiosInstance.get(url);
            const data = response.data;

            setReportData(data);

            setMetaData({
                current_page: data.classes.current_page,
                per_page: data.classes.per_page,
                total: data.classes.total,
                last_page: data.classes.last_page,
            });
        } catch (err) {
            setReportData(null);
            setMetaData(null);
            setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Reset the state
     */
    const resetState = () => {
        setReportData(undefined);
        setMetaData(undefined);
        setError("");
        setLoading(false);
    };

    return { loading, error, reportData, getTeacherReportById, metaData, resetState };
};